import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Button,
  TableFooter,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Stack,
  Tooltip,
  TablePagination,
  Paper,
  Box,
  Chip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DevicesIcon from '@mui/icons-material/Devices';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useTheme } from '@mui/material/styles';

import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionFab from './components/CollectionFab';
import CollectionActions from './components/CollectionActions';
import TableShimmer from '../common/components/TableShimmer';
import SearchHeader from './components/SearchHeader';
import { formatStatus, formatTime } from '../common/util/formatter';
import { useAdministrator, useDeviceReadonly, useManager } from '../common/util/permissions';
import useSettingsStyles from './common/useSettingsStyles';
import DeviceUsersValue from './components/DeviceUsersValue';
import usePersistedState from '../common/util/usePersistedState';
import fetchOrThrow from '../common/util/fetchOrThrow';
import AddressValue from '../common/components/AddressValue';
import exportExcel from '../common/util/exportExcel';

const NO_GROUP_KEY = '__NO_GROUP__';
const SEARCH_DEBOUNCE = 400;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_ROWS_PER_PAGE = 50;
const MINUTE = 60 * 1000;

const KPI_FILTERS = {
  ALL: 'ALL',
  OUT_24H: 'OUT_24H',
  OUT_72H: 'OUT_72H',
  NO_DATA: 'NO_DATA',
  IDLE_10M: 'IDLE_10M',
};

const STATUS_META = {
  ON_MOVING: {
    key: 'ON_MOVING',
    label: 'Em movimento',
    uiColor: '#28A745',
    mapColor: 'success',
    icon: <AutorenewIcon fontSize="small" />,
  },
  OFF: {
    key: 'OFF',
    label: 'Ignição desligada',
    uiColor: '#DC3545',
    mapColor: 'error',
    icon: <PowerSettingsNewIcon fontSize="small" />,
  },
  ON_STOPPED: {
    key: 'ON_STOPPED',
    label: 'Parado c/ motor ligado',
    uiColor: '#FFC107',
    mapColor: 'warning',
    icon: <PauseCircleFilledIcon fontSize="small" />,
  },
  IDLE_10M: {
    key: 'IDLE_10M',
    label: 'Ocioso > 10 min',
    uiColor: '#FD7E14',
    mapColor: 'warningDark',
    icon: <ScheduleIcon fontSize="small" />,
  },
  OUT_24H: {
    key: 'OUT_24H',
    label: 'Desconectado > 24h',
    uiColor: '#0B3D91',
    mapColor: 'infoDark',
    icon: <WifiOffIcon fontSize="small" />,
  },
  OUT_72H: {
    key: 'OUT_72H',
    label: 'Desconectado > 72h',
    uiColor: '#6F42C1',
    mapColor: 'purple',
    icon: <SignalWifiOffIcon fontSize="small" />,
  },
  NO_DATA: {
    key: 'NO_DATA',
    label: 'Sem dados',
    uiColor: '#6C757D',
    mapColor: 'neutral',
    icon: <ErrorOutlineIcon fontSize="small" />,
  },
};

function useDebouncedValue(value, delayMs = SEARCH_DEBOUNCE) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

function HighlightText({ text, keyword }) {
  const str = text ?? '';
  const k = (keyword ?? '').trim();

  if (!k) return <>{str}</>;

  const lower = str.toLowerCase();
  const kLower = k.toLowerCase();
  const parts = [];
  let start = 0;

  while (true) {
    const idx = lower.indexOf(kLower, start);
    if (idx === -1) break;

    if (idx > start) {
      parts.push({ type: 'text', value: str.slice(start, idx) });
    }

    parts.push({ type: 'hit', value: str.slice(idx, idx + k.length) });
    start = idx + k.length;
  }

  if (start < str.length) {
    parts.push({ type: 'text', value: str.slice(start) });
  }

  return (
    <>
      {parts.map((part, index) => (
        part.type === 'hit' ? (
          <strong key={index}>{part.value}</strong>
        ) : (
          <React.Fragment key={index}>{part.value}</React.Fragment>
        )
      ))}
    </>
  );
}

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

const matchesKeyword = (indexedText, keyword) => {
  if (!keyword) return true;

  const tokens = keyword
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (!tokens.length) return true;

  return tokens.every((token) => indexedText.includes(token));
};

const toKph = (knots) => {
  if (knots == null) return null;
  const n = Number(knots);
  if (Number.isNaN(n)) return null;
  return n * 1.852;
};

const getPositionTimeMs = (position) => {
  const raw = position?.fixTime
    ?? position?.deviceTime
    ?? position?.serverTime
    ?? position?.time
    ?? position?.timestamp;

  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};

const getLastUpdate = (device, position) => (
  position?.fixTime
  || position?.deviceTime
  || position?.serverTime
  || position?.time
  || position?.timestamp
  || device?.lastUpdate
  || null
);

const getOperationalStatusKey = (device, position) => {
  const lastUpdate = getLastUpdate(device, position);

  if (!lastUpdate) {
    return 'NO_DATA';
  }

  const hours = dayjs().diff(dayjs(lastUpdate), 'hour');

  if (hours >= 72) {
    return 'OUT_72H';
  }

  if (hours >= 24) {
    return 'OUT_24H';
  }

  const ignition = position?.attributes?.ignition ?? null;
  const speedKph = toKph(position?.speed);
  const moving = speedKph != null && speedKph > 2;

  const posTime = getPositionTimeMs(position);
  const ageMs = posTime ? (Date.now() - posTime) : null;
  const idle10m = ignition === true && !moving && ageMs != null && ageMs >= 10 * MINUTE;

  if (ignition === false) {
    return 'OFF';
  }

  if (moving) {
    return 'ON_MOVING';
  }

  if (idle10m) {
    return 'IDLE_10M';
  }

  if (ignition === true) {
    return 'ON_STOPPED';
  }

  return 'NO_DATA';
};

const getOperationalStatusMeta = (device, position) => {
  const key = getOperationalStatusKey(device, position);
  return STATUS_META[key] || STATUS_META.NO_DATA;
};

const KpiCard = memo(({
  title,
  value,
  subtitle,
  icon,
  color,
  active = false,
  onClick,
}) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: active ? color : 'divider',
      backgroundColor: 'background.paper',
      height: '100%',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'all 0.2s ease',
      boxShadow: active ? `0 0 0 2px ${color}22` : 'none',
      '&:hover': {
        borderColor: color,
        transform: 'translateY(-1px)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}14`,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </Typography>

        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  </Paper>
));

KpiCard.displayName = 'KpiCard';

const DevicesKpis = memo(({
  totalDevices,
  filteredDevices,
  totalGroups,
  stale24Count,
  stale72Count,
  noDataCount,
  idle10mCount,
  hasActiveFilter,
  activeKpiFilter,
  onKpiFilterChange,
  t,
}) => (
  <Box
    sx={{
      mb: 2,
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
        xl: 'repeat(5, minmax(0, 1fr))',
      },
      gap: 2,
    }}
  >
    <KpiCard
      title={t('deviceTitle') || 'Dispositivos'}
      value={totalDevices.toLocaleString('pt-BR')}
      subtitle={
        hasActiveFilter
          ? `${filteredDevices.toLocaleString('pt-BR')} em ${totalGroups.toLocaleString('pt-BR')} grupos`
          : 'Total cadastrado'
      }
      icon={<DevicesIcon fontSize="medium" />}
      color="#28A745"
      active={activeKpiFilter === KPI_FILTERS.ALL}
      onClick={() => onKpiFilterChange(KPI_FILTERS.ALL)}
    />

    <KpiCard
      title="Desconectado > 24h"
      value={stale24Count.toLocaleString('pt-BR')}
      subtitle="24h a 72h"
      icon={<AccessTimeIcon fontSize="medium" />}
      color={STATUS_META.OUT_24H.uiColor}
      active={activeKpiFilter === KPI_FILTERS.OUT_24H}
      onClick={() => onKpiFilterChange(KPI_FILTERS.OUT_24H)}
    />

    <KpiCard
      title="Desconectado > 72h"
      value={stale72Count.toLocaleString('pt-BR')}
      subtitle="Acima de 72h"
      icon={<SignalWifiOffIcon fontSize="medium" />}
      color={STATUS_META.OUT_72H.uiColor}
      active={activeKpiFilter === KPI_FILTERS.OUT_72H}
      onClick={() => onKpiFilterChange(KPI_FILTERS.OUT_72H)}
    />

    <KpiCard
      title="Sem dados"
      value={noDataCount.toLocaleString('pt-BR')}
      subtitle="Sem atualização"
      icon={<ErrorOutlineIcon fontSize="medium" />}
      color={STATUS_META.NO_DATA.uiColor}
      active={activeKpiFilter === KPI_FILTERS.NO_DATA}
      onClick={() => onKpiFilterChange(KPI_FILTERS.NO_DATA)}
    />

    <KpiCard
      title="Ocioso > 10 min"
      value={idle10mCount.toLocaleString('pt-BR')}
      subtitle="Ligado sem mover"
      icon={<ScheduleIcon fontSize="medium" />}
      color={STATUS_META.IDLE_10M.uiColor}
      active={activeKpiFilter === KPI_FILTERS.IDLE_10M}
      onClick={() => onKpiFilterChange(KPI_FILTERS.IDLE_10M)}
    />
  </Box>
));

DevicesKpis.displayName = 'DevicesKpis';

const StatusChip = memo(({ status }) => (
  <Chip
    size="small"
    label={status.label}
    icon={status.icon}
    sx={{
      maxWidth: 220,
      color: '#fff',
      backgroundColor: status.uiColor,
      '& .MuiChip-icon': {
        color: '#fff',
      },
    }}
  />
));

StatusChip.displayName = 'StatusChip';

const GroupHeaderRow = memo(({
  groupKey,
  groupName,
  listLength,
  expanded,
  isSearching,
  toggleGroup,
  totalColumns,
  t,
  keyword,
}) => (
  <TableRow
    hover
    onClick={() => toggleGroup(groupKey)}
    sx={{ cursor: isSearching ? 'default' : 'pointer' }}
  >
    <TableCell colSpan={totalColumns}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip
          title={
            isSearching
              ? (t('sharedSearch') || 'Busca ativa')
              : expanded
                ? (t('sharedCollapse') || 'Recolher')
                : (t('sharedExpand') || 'Expandir')
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={isSearching}
              onClick={(e) => {
                e.stopPropagation();
                toggleGroup(groupKey);
              }}
            >
              {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <Typography variant="subtitle2">
          <HighlightText text={groupName} keyword={keyword} /> ({listLength})
        </Typography>

        {!expanded && !isSearching && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {t('sharedHidden') || 'oculto'}
          </Typography>
        )}

        {isSearching && (
          <Typography variant="body2" sx={{ opacity: 0.65 }}>
            {t('sharedShowingResults') || 'mostrando resultados'}
          </Typography>
        )}
      </Stack>
    </TableCell>
  </TableRow>
));

GroupHeaderRow.displayName = 'GroupHeaderRow';

const DeviceDataRow = memo(({
  item,
  keyword,
  groups,
  positions,
  manager,
  readonly,
  disableRemove,
  classes,
  actionConnections,
  onReload,
}) => {
  const position = positions?.[item.id];
  const status = getOperationalStatusMeta(item, position);

  return (
    <TableRow>
      <TableCell>
        <HighlightText text={item.name} keyword={keyword} />
      </TableCell>

      <TableCell>
        <HighlightText text={item.uniqueId} keyword={keyword} />
      </TableCell>

      <TableCell>
        <StatusChip status={status} />
      </TableCell>

      <TableCell>
        <HighlightText
          text={item.groupId ? groups?.[item.groupId]?.name : ''}
          keyword={keyword}
        />
      </TableCell>

      <TableCell>
        <HighlightText text={item.phone} keyword={keyword} />
      </TableCell>

      <TableCell>
        <HighlightText text={item.model} keyword={keyword} />
      </TableCell>

      <TableCell>
        <HighlightText text={item.contact} keyword={keyword} />
      </TableCell>

      <TableCell>{formatTime(item.expirationTime, 'date')}</TableCell>

      <TableCell>
        {position && (
          <AddressValue
            latitude={position.latitude}
            longitude={position.longitude}
            originalAddress={position?.address}
          />
        )}
      </TableCell>

      {manager && (
        <TableCell>
          <DeviceUsersValue deviceId={item.id} />
        </TableCell>
      )}

      <TableCell className={classes.columnAction} padding="none">
        <CollectionActions
          itemId={item.id}
          editPath="/settings/device"
          endpoint="devices"
          setTimestamp={onReload}
          customActions={[actionConnections]}
          readonly={readonly}
          disableRemove={disableRemove}
        />
      </TableCell>
    </TableRow>
  );
});

DeviceDataRow.displayName = 'DeviceDataRow';

const DevicesPage = () => {
  const { classes } = useSettingsStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const t = useTranslation();

  const groups = useSelector((state) => state.groups.items);
  const positions = useSelector((state) => state.session.positions);

  const administrator = useAdministrator();
  const deviceReadonly = useDeviceReadonly();
  const manager = useManager();
  const canEditDevice = manager || deviceReadonly;
  const disableRemove = !administrator;

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeKpiFilter, setActiveKpiFilter] = useState(KPI_FILTERS.ALL);
  const debouncedKeyword = useDebouncedValue(searchKeyword, SEARCH_DEBOUNCE);

  const [showAll, setShowAll] = usePersistedState('showAllDevices', false);
  const [loading, setLoading] = useState(false);
  const [expandedByGroup, setExpandedByGroup] = usePersistedState('devicesGroupsExpanded', {});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ all: showAll });
      const response = await fetchOrThrow(`/api/devices?${query.toString()}`);
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [timestamp, showAll]);

  useEffect(() => {
    setPage(0);
  }, [debouncedKeyword, showAll, activeKpiFilter]);

  const totalColumns = manager ? 11 : 10;

  const getGroupName = useCallback((key) => {
    if (key === NO_GROUP_KEY) return t('sharedNoGroup') || 'Sem grupo';
    return groups?.[key]?.name || t('sharedUnknown') || 'Grupo desconhecido';
  }, [groups, t]);

  const indexedItems = useMemo(() => (
    items.map((item) => {
      const groupName = item.groupId ? groups?.[item.groupId]?.name : '';
      const position = positions?.[item.id];
      const positionAddress = position?.address || '';
      const status = getOperationalStatusMeta(item, position);

      return {
        ...item,
        _statusKey: status.key,
        _searchIndex: [
          item.name,
          item.uniqueId,
          groupName,
          item.phone,
          item.model,
          item.contact,
          positionAddress,
          status.label,
          status.key,
        ].filter(Boolean).join(' ').toLowerCase(),
      };
    })
  ), [items, groups, positions]);

  const isSearching = useMemo(
    () => Boolean(debouncedKeyword?.trim()),
    [debouncedKeyword],
  );

  const normalizedKeyword = useMemo(
    () => normalizeText(debouncedKeyword),
    [debouncedKeyword],
  );

  const keywordFilteredItems = useMemo(() => (
    indexedItems.filter((item) => matchesKeyword(item._searchIndex, normalizedKeyword))
  ), [indexedItems, normalizedKeyword]);

  const filteredItems = useMemo(() => {
    if (activeKpiFilter === KPI_FILTERS.ALL) {
      return keywordFilteredItems;
    }

    return keywordFilteredItems.filter((item) => item._statusKey === activeKpiFilter);
  }, [keywordFilteredItems, activeKpiFilter]);

  const totalFiltered = filteredItems.length;
  const totalDevices = items.length;

  const stale24Count = useMemo(() => (
    items.filter((item) => {
      const lastUpdate = getLastUpdate(item, positions?.[item.id]);
      if (!lastUpdate) return false;

      const hours = dayjs().diff(dayjs(lastUpdate), 'hour', true);
      return Number.isFinite(hours) && hours >= 24 && hours < 72;
    }).length
  ), [items, positions]);

  const stale72Count = useMemo(() => (
    items.filter((item) => {
      const lastUpdate = getLastUpdate(item, positions?.[item.id]);
      if (!lastUpdate) return false;

      const hours = dayjs().diff(dayjs(lastUpdate), 'hour', true);
      return Number.isFinite(hours) && hours >= 72;
    }).length
  ), [items, positions]);

  const noDataCount = useMemo(() => (
    items.filter((item) => !getLastUpdate(item, positions?.[item.id])).length
  ), [items, positions]);

  const idle10mCount = useMemo(() => (
    items.filter((item) => getOperationalStatusKey(item, positions?.[item.id]) === 'IDLE_10M').length
  ), [items, positions]);

  const grouped = useMemo(() => {
    const map = new Map();

    for (const item of filteredItems) {
      const key = item.groupId || NO_GROUP_KEY;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(item);
    }

    const entries = Array.from(map.entries()).map(([key, list]) => [
      key,
      list.slice().sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    ]);

    entries.sort((a, b) => getGroupName(a[0]).localeCompare(getGroupName(b[0])));

    const groupKeys = entries.map(([key]) => key);
    const total = entries.reduce((acc, [, list]) => acc + list.length, 0);

    return {
      entries,
      groupKeys,
      total,
      groupsCount: entries.length,
    };
  }, [filteredItems, getGroupName]);

  const safePage = useMemo(() => {
    if (grouped.total === 0) {
      return 0;
    }

    const maxPage = Math.max(0, Math.ceil(grouped.total / rowsPerPage) - 1);
    return Math.min(page, maxPage);
  }, [grouped.total, page, rowsPerPage]);

  const pagedGroupedEntries = useMemo(() => {
    const startIndex = safePage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    let currentIndex = 0;
    const result = [];

    for (const [groupKey, list] of grouped.entries) {
      const groupStart = currentIndex;
      const groupEnd = currentIndex + list.length;

      if (groupEnd > startIndex && groupStart < endIndex) {
        const sliceStart = Math.max(0, startIndex - groupStart);
        const sliceEnd = Math.min(list.length, endIndex - groupStart);
        result.push([groupKey, list.slice(sliceStart, sliceEnd), list.length]);
      }

      currentIndex = groupEnd;
      if (currentIndex >= endIndex) {
        break;
      }
    }

    return result;
  }, [grouped.entries, safePage, rowsPerPage]);

  const isGroupExpanded = useCallback((groupKey) => {
    if (isSearching || activeKpiFilter !== KPI_FILTERS.ALL) return true;
    const value = expandedByGroup?.[groupKey];
    return value === undefined ? true : Boolean(value);
  }, [expandedByGroup, isSearching, activeKpiFilter]);

  const toggleGroup = useCallback((groupKey) => {
    if (isSearching || activeKpiFilter !== KPI_FILTERS.ALL) return;

    setExpandedByGroup((prev) => {
      const current = prev || {};
      const currentValue = current[groupKey];
      const nextValue = currentValue === undefined ? false : !currentValue;
      return { ...current, [groupKey]: nextValue };
    });
  }, [setExpandedByGroup, isSearching, activeKpiFilter]);

  const expandAll = useCallback(() => {
    const next = {};
    grouped.groupKeys.forEach((key) => {
      next[key] = true;
    });
    setExpandedByGroup((prev) => ({ ...(prev || {}), ...next }));
  }, [grouped.groupKeys, setExpandedByGroup]);

  const collapseAll = useCallback(() => {
    const next = {};
    grouped.groupKeys.forEach((key) => {
      next[key] = false;
    });
    setExpandedByGroup((prev) => ({ ...(prev || {}), ...next }));
  }, [grouped.groupKeys, setExpandedByGroup]);

  const handleExport = useCallback(async () => {
    const data = filteredItems.map((item) => {
      const status = getOperationalStatusMeta(item, positions?.[item.id]);

      return {
        [t('sharedName')]: item.name,
        [t('deviceIdentifier')]: item.uniqueId,
        Status: status.label,
        [t('groupParent')]: item.groupId ? groups[item.groupId]?.name : null,
        [t('sharedPhone')]: item.phone,
        [t('deviceModel')]: item.model,
        [t('deviceContact')]: item.contact,
        [t('userExpirationTime')]: formatTime(item.expirationTime, 'date'),
        [t('deviceStatus')]: formatStatus(item.status, t),
        [t('deviceLastUpdate')]: formatTime(item.lastUpdate, 'minutes'),
        [t('positionAddress')]: positions[item.id]?.address || '',
      };
    });

    const sheets = new Map();
    sheets.set(t('deviceTitle'), data);
    await exportExcel(t('deviceTitle'), 'devices.xlsx', sheets, theme);
  }, [filteredItems, groups, positions, t, theme]);

  const handleReload = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleKpiFilterChange = useCallback((filter) => {
    setActiveKpiFilter((prev) => (prev === filter ? KPI_FILTERS.ALL : filter));
  }, []);

  const hasAnyTopFilter = activeKpiFilter !== KPI_FILTERS.ALL;
  const hasAnyFilter = isSearching || hasAnyTopFilter;

  const actionConnections = useMemo(() => ({
    key: 'connections',
    title: t('sharedConnections'),
    icon: <LinkIcon fontSize="small" />,
    handler: (deviceId) => navigate(`/settings/device/${deviceId}/connections`),
  }), [navigate, t]);

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'deviceTitle']}>
      {!loading && (
        <DevicesKpis
          totalDevices={totalDevices}
          filteredDevices={totalFiltered}
          totalGroups={grouped.groupsCount}
          stale24Count={stale24Count}
          stale72Count={stale72Count}
          noDataCount={noDataCount}
          idle10mCount={idle10mCount}
          hasActiveFilter={hasAnyFilter}
          activeKpiFilter={activeKpiFilter}
          onKpiFilterChange={handleKpiFilterChange}
          t={t}
        />
      )}

      <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            onClick={expandAll}
            variant="text"
            size="small"
            disabled={isSearching || hasAnyTopFilter}
          >
            {t('sharedExpandAll') || 'Expandir todos'}
          </Button>

          <Button
            onClick={collapseAll}
            variant="text"
            size="small"
            disabled={isSearching || hasAnyTopFilter}
          >
            {t('sharedCollapseAll') || 'Recolher todos'}
          </Button>

          {hasAnyTopFilter && (
            <Chip
              size="small"
              label={`Filtro: ${STATUS_META[activeKpiFilter]?.label || 'Todos'}`}
              onDelete={() => setActiveKpiFilter(KPI_FILTERS.ALL)}
            />
          )}

          {isSearching && (
            <Typography variant="body2" sx={{ opacity: 0.75, ml: 1 }}>
              {(t('sharedTotal') || 'Total')}: {grouped.total} — “{debouncedKeyword}”
            </Typography>
          )}
        </Stack>

        {!isSearching && (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {(t('sharedTotal') || 'Total')}: {grouped.total}
          </Typography>
        )}
      </Stack>

      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>{t('sharedName')}</TableCell>
            <TableCell>{t('deviceIdentifier')}</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>{t('groupParent')}</TableCell>
            <TableCell>{t('sharedPhone')}</TableCell>
            <TableCell>{t('deviceModel')}</TableCell>
            <TableCell>{t('deviceContact')}</TableCell>
            <TableCell>{t('userExpirationTime')}</TableCell>
            <TableCell>{t('positionAddress')}</TableCell>
            {manager && <TableCell>{t('settingsUsers')}</TableCell>}
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableShimmer columns={manager ? 10 : 9} endAction />
          ) : pagedGroupedEntries.length ? (
            pagedGroupedEntries.map(([groupKey, list, fullGroupLength]) => {
              const expanded = isGroupExpanded(groupKey);

              return (
                <React.Fragment key={`group-${groupKey}`}>
                  <GroupHeaderRow
                    groupKey={groupKey}
                    groupName={getGroupName(groupKey)}
                    listLength={fullGroupLength}
                    expanded={expanded}
                    isSearching={isSearching || hasAnyTopFilter}
                    toggleGroup={toggleGroup}
                    totalColumns={totalColumns}
                    t={t}
                    keyword={debouncedKeyword}
                  />

                  {expanded && list.map((item) => (
                    <DeviceDataRow
                      key={item.id}
                      item={item}
                      keyword={debouncedKeyword}
                      groups={groups}
                      positions={positions}
                      manager={manager}
                      readonly={!canEditDevice}
                      disableRemove={disableRemove}
                      classes={classes}
                      actionConnections={actionConnections}
                      onReload={handleReload}
                    />
                  ))}
                </React.Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={totalColumns}>
                <Typography variant="body2">
                  {t('sharedNoResults') || 'Nenhum resultado.'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TableCell>
              <Button
                onClick={handleExport}
                variant="text"
                disabled={loading || !filteredItems.length}
              >
                {t('reportExport')}
              </Button>
            </TableCell>

            <TableCell colSpan={manager ? 5 : 4}>
              {!loading && grouped.total > 0 && (
                <TablePagination
                  component="div"
                  count={grouped.total}
                  page={safePage}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  labelRowsPerPage="Itens por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
              )}
            </TableCell>

            <TableCell colSpan={manager ? 5 : 5} align="right">
              <FormControlLabel
                control={(
                  <Switch
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                    size="small"
                  />
                )}
                label={t('notificationAlways')}
                labelPlacement="start"
                disabled={!manager}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      {manager && <CollectionFab editPath="/settings/device" />}
    </PageLayout>
  );
};

export default DevicesPage;
